import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createBranch, deleteBranch, fetchBranches, updateBranch } from '../../api/salon';
import BranchFormPanel from '../../components/salon/BranchFormPanel';
import DashboardShell from '../../components/ui/DashboardShell';
import { useToast } from '../../context/ToastContext';

const isOpenNow = (hours) => {
  const today = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date().getDay()];
  const row = hours?.find((h) => h.day === today);
  if (!row || row.isClosed) return false;
  const now = new Date().toTimeString().slice(0, 5);
  return now >= row.openTime && now <= row.closeTime;
};

const SalonBranchesPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['salon-branches'],
    queryFn: fetchBranches,
  });

  const branches = data?.branches || [];

  const saveMut = useMutation({
    mutationFn: ({ id, payload }) =>
      id
        ? updateBranch(id, payload.fields, payload.coverFile)
        : createBranch(payload.fields, payload.coverFile),
    onSuccess: () => {
      showToast(editing ? t('toast.branchUpdated') : t('toast.branchCreated'), 'success');
      setPanelOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['salon-branches'] });
      queryClient.invalidateQueries({ queryKey: ['salon-profile'] });
    },
    onError: (err) => {
      showToast(err.response?.data?.message || t('errors.saveFailed'), 'error');
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => {
      showToast(t('toast.branchHidden'), 'success');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['salon-branches'] });
    },
    onError: (err) => {
      showToast(err.response?.data?.message || t('errors.deleteFailed'), 'error');
    },
  });

  const handleSave = ({ name, address, phone, email, description, operatingHours, coverFile, latitude, longitude }) => {
    if (latitude == null || longitude == null) {
      showToast(t('salon.branches.coordsRequired'), 'error');
      return;
    }
    const fields = {
      name,
      address,
      phone,
      email,
      description,
      operatingHours,
      latitude,
      longitude,
    };
    saveMut.mutate({
      id: editing?._id,
      payload: { fields, coverFile },
    });
  };

  return (
    <DashboardShell
      title={t('salon.branches.yourBranches')}
      subtitle={t('salon.branches.activeLocations', { count: branches.length })}
    >
      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setPanelOpen(true);
          }}
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white hover:bg-glow-500"
        >
          {t('salon.branches.addBranchButton')}
        </button>
      </div>

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('salon.branches.loading')}</p>}

      <div className="grid gap-6 md:grid-cols-2">
        {branches.map((branch) => {
          const open = isOpenNow(branch.operatingHours);
          return (
            <div
              key={branch._id}
              className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
            >
              <div className="relative h-40 bg-zinc-100 dark:bg-zinc-800">
                {branch.coverImage ? (
                  <img src={branch.coverImage} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-zinc-600 dark:text-zinc-500">{t('salon.branches.noCover')}</div>
                )}
                {branch.isMain && (
                  <span className="absolute left-3 top-3 rounded-full bg-amber-500/90 px-2 py-0.5 text-xs font-medium text-black">
                    {t('salon.branches.mainBranchBadge')}
                  </span>
                )}
                <span
                  className={`absolute right-3 top-3 rounded-full px-2 py-0.5 text-xs ${
                    open ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-700 text-zinc-600 dark:text-zinc-400'
                  }`}
                >
                  {open ? t('explore.openNow') : t('explore.closedNow')}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-medium">{branch.name}</h3>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{branch.address || t('salon.branches.noAddress')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={`/salon/branches/${branch._id}/gallery`}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    {t('salon.branches.galleryLink')}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(branch);
                      setPanelOpen(true);
                    }}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    {t('common.edit')}
                  </button>
                  {!branch.isMain && (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(branch)}
                      className="rounded-lg border border-red-900/50 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!isLoading && !branches.length && (
        <p className="text-center text-zinc-600 dark:text-zinc-500">{t('salon.branches.addFirstLocation')}</p>
      )}

      <BranchFormPanel
        open={panelOpen}
        onClose={() => {
          setPanelOpen(false);
          setEditing(null);
        }}
        branch={editing}
        onSave={handleSave}
        saving={saveMut.isPending}
      />

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-w-md rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
            <h3 className="text-lg font-medium">{t('salon.branches.hideBranchTitle')}</h3>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
              {t('salon.branches.hideBranchDesc', { name: deleteConfirm.name })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setDeleteConfirm(null)} className="text-sm text-zinc-600 dark:text-zinc-400">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => deleteMut.mutate(deleteConfirm._id)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white"
              >
                {t('salon.branches.hideBranchConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default SalonBranchesPage;
